import { Save } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components";
import { ErrorPlaceholder } from "../../../components/common";
import FormHeader from "../../../components/form/FormHeader";
import { goBackOrGoHome } from "../../../utils/navigation";
import { NotificationForm } from "../components/form";
import { NotificationFormSkeleton } from "../components/notification-skeletons";
import { useGetNotificationDetail } from "../hooks/useGetNotificationDetail";
import { useUpdateNotification } from "../hooks/useUpdateNotification";
import type { CreateNotificationFormValues } from "../components/form";

function EditNotificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formId = "notification-edit-form";

  const {
    notification,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch,
  } = useGetNotificationDetail(id);
  const {
    loading: isUpdating,
    error: updateError,
    success,
    updateNotification,
  } = useUpdateNotification();
  const defaultValues = useMemo<Partial<CreateNotificationFormValues> | undefined>(() => {
    if (!notification) {
      return undefined;
    }

    return {
      title: notification.title ?? "",
      content: notification.content ?? "",
      alert_type_id: notification.alert_type_id ?? "",
      timezone: notification.timezone ?? undefined,
      is_pinned: Boolean(notification.is_pinned),
    };
  }, [notification]);
  const alertTypeLabel = useMemo(() => {
    if (!notification) {
      return "";
    }

    const name = notification.alert_type?.name ?? notification.alert_type_id ?? "-";

    if (!notification.alert_type?.icon) {
      return name;
    }

    return `${notification.alert_type.icon} ${name}`;
  }, [notification]);

  useEffect(() => {
    if (!id) {
      navigate("/notification", { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!success) {
      return;
    }

    navigate("/notification");
  }, [navigate, success]);

  const handleSubmit = async (values: CreateNotificationFormValues) => {
    if (!id) {
      return;
    }

    await updateNotification(id, {
      title: values.title,
      content: values.content,
      timezone: values.timezone,
      is_pinned: values.is_pinned,
    });
  };

  const isSaving = isUpdating || isLoadingDetail;
  const detailMessage = detailError;

  if (!id) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title={t("notification.edit_notification")}
        subtitle=""
        backLabel={t("common.back")}
        actions={
          <Button
            type="submit"
            form={formId}
            disabled={isSaving}
            className="flex items-center gap-2 bg-emerald-500 text-white"
          >
            <Save className="h-4 w-4" />
            {isUpdating ? t("common.saving") : t("common.save_changes")}
          </Button>
        }
      />

      {isLoadingDetail && <NotificationFormSkeleton />}

      {!isLoadingDetail && detailMessage && (
        <div className="px-6 py-4">
          <ErrorPlaceholder
            error={detailMessage}
            onRetry={() => void refetch()}
          />
        </div>
      )}

      {!isLoadingDetail && notification && (
        <NotificationForm
          formId={formId}
          loading={isSaving}
          error={updateError}
          alertTypes={[]}
          alertTypesLoading={false}
          alertTypesError={null}
          defaultValues={defaultValues}
          showAlertTypeSelector={false}
          readOnlyAlertTypeLabel={alertTypeLabel}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

export default EditNotificationPage;
